import { useState, useEffect } from 'react'
import { matchesSearch } from '@/lib/search'
import { MainLayout } from '@/components/layout'
import { ContextIcon } from '@/components/ui'
import { EntityListPage, DataCard, type FilterField, type DataTableColumn } from '@/components/common'
import { InvitationsList, ContextUpdateModal, GroupUpdateModal, ActivityUpdateModal, TeamModal, DefaultMarginUpdateModal } from '@/components/units'
import { useAuth } from '@/contexts/AuthContext'
import { useTranslation, useUnitTranslation } from '@/contexts/TranslationContext'
import { useContextKey } from '@/contexts/UnitContext'
import {
  getUserUnits, createUnit, updateUnit, deleteUnit, isUnitAdmin,
  getUserInvitations, acceptInvitation, declineInvitation
} from '@/lib/services/units'
import { updateUnitContext, saveEnabledGroups } from '@/lib/services/contexts'
import { appConfig } from '@/lib/config'
import type { UserUnit, UnitContext } from '@/lib/types'

interface UnitFormData {
  name: string
  description: string
  pix_key: string
}

function generateSlug(text: string): string {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
}

export default function Units() {
  const { user: authUser } = useAuth()
  const { t } = useTranslation()
  const { tu } = useUnitTranslation()
  const appContext = useContextKey() as UnitContext
  const fixedContext = appConfig.fixedContext as UnitContext | null

  const [invitations, setInvitations] = useState<any[]>([])
  const [teamUnit, setTeamUnit] = useState<UserUnit | null>(null)
  const [contextUnit, setContextUnit] = useState<UserUnit | null>(null)
  const [groupsUnit, setGroupsUnit] = useState<UserUnit | null>(null)
  const [activityUnit, setActivityUnit] = useState<UserUnit | null>(null)
  const [marginUnit, setMarginUnit] = useState<UserUnit | null>(null)
  // Trigger reload of the EntityListPage after modal saves
  const [reloadKey, setReloadKey] = useState(0)

  useEffect(() => {
    if (authUser) {
      getUserInvitations().then(setInvitations).catch(console.error)
    }
  }, [authUser])

  const reloadInvitations = async () => {
    if (authUser) {
      const inv = await getUserInvitations()
      setInvitations(inv)
      setReloadKey(k => k + 1)
    }
  }

  if (!authUser) return null

  // --- EntityListPage config ---
  const config = {
    entityType: `units-${reloadKey}`,
    filterFields: [] as FilterField[],
    loadData: async () => {
      const units = await getUserUnits(authUser.id)
      // If a fixed context is set in env, only show units of that context
      if (fixedContext) {
        return units.filter(u => u.context_key === fixedContext)
      }
      return units
    },
    createEntity: async (data: UnitFormData): Promise<UserUnit> => {
      const unit = await createUnit(data.name, generateSlug(data.name), {
        description: data.description || undefined,
        pix_key: data.pix_key || undefined
      })
      const resultUnit = { ...unit, role: 'admin' as const, context_key: fixedContext }
      // Auto-assign fixed context immediately after creation
      if (fixedContext) {
        await updateUnitContext(unit.id, fixedContext)
      }
      return resultUnit
    },
    updateEntity: async (id: string, data: UnitFormData): Promise<UserUnit> => {
      const unit = await updateUnit(id, {
        name: data.name,
        description: data.description || undefined,
        pix_key: data.pix_key || undefined
      })
      return unit as unknown as UserUnit
    },
    deleteEntity: async (id: string) => {
      const isAdmin = await isUnitAdmin(id, authUser.id)
      if (!isAdmin) throw new Error(tu('record.noPermission', { unit: tu('singular').toLowerCase() }))
      await deleteUnit(id)
    },
    applyFilters: (entities: UserUnit[], searchQuery: string) => {
      if (!searchQuery || !searchQuery.trim()) return entities
      return entities.filter(u =>
        matchesSearch(u.name, searchQuery) ||
        matchesSearch(u.description || '', searchQuery)
      )
    },
    validateForm: (data: UnitFormData) => {
      const errors: Record<string, string> = {}
      if (!data.name || data.name.trim().length < 3) {
        errors.name = 'Nome deve ter pelo menos 3 caracteres'
      }
      return errors
    }
  }

  const labels = {
    title: tu('list.title'),
    description: tu('list.description'),
    newButton: tu('list.createNew'),
    searchPlaceholder: `${t('actions.search')} ${tu('plural').toLowerCase()}...`,
    emptyTitle: tu('list.noUnits'),
    emptyDescription: tu('list.startByCreating'),
    modalTitleNew: `${t('actions.create')} ${tu('singular')}`,
    modalTitleEdit: `${t('actions.edit')} ${tu('singular')}`,
    modalTitleView: tu('singular'),
    recordTitle: tu('singular'),
    deleteTitle: `${t('actions.delete')} ${tu('singular')}`,
    deleteMessage: tu('list.deleteConfirm', { unit: tu('singular').toLowerCase(), name: '' }),
    deleteButton: t('actions.delete'),
    cancelButton: t('actions.cancel'),
    submitButton: t('actions.create'),
    updateButton: t('actions.save'),
    viewCard: 'Cards',
    viewTable: 'Tabela',
    clearSearch: t('actions.filter')
  }

  const getRoleLabel = (role: string) => {
    if (role === 'admin') return tu('list.admin')
    if (role === 'manager') return tu('list.manager')
    return tu('list.viewer')
  }

  const filterFields: FilterField[] = [
    {
      key: 'name',
      label: tu('record.nameLabel'),
      type: 'text',
      placeholder: `${t('actions.search')} ${tu('plural').toLowerCase()}...`
    }
  ]

  const tableColumns: DataTableColumn<UserUnit>[] = [
    {
      key: 'name',
      label: tu('record.nameLabel'),
      render: (unit) => (
        <div className="flex items-center gap-3">
          <ContextIcon
            context={(unit.context_key as UnitContext) || appContext}
            className="h-5 w-5 shrink-0 text-emerald-600 dark:text-emerald-400"
          />
          <div>
            <div className="font-medium text-slate-900 dark:text-white">{unit.name}</div>
            {unit.description && (
              <div className="text-sm text-slate-500 dark:text-slate-400 truncate max-w-xs">{unit.description}</div>
            )}
          </div>
        </div>
      )
    },
    {
      key: 'role',
      label: 'Papel',
      render: (unit) => (
        <span className="text-sm text-slate-700 dark:text-slate-300">{getRoleLabel(unit.role)}</span>
      )
    },
    // Only show context column in multi-context mode
    ...(!fixedContext ? [{
      key: 'context_key',
      label: 'Contexto',
      render: (unit: UserUnit) => (
        <span className="text-sm text-slate-700 dark:text-slate-300 capitalize">
          {unit.context_key ? t(`contexts.${unit.context_key}`) : '—'}
        </span>
      )
    }] : [])
  ]

  const renderCard = (
    unit: UserUnit,
    onEdit: (u: UserUnit) => void,
    onDelete: (u: UserUnit) => void
  ) => {
    const isAdmin = unit.role === 'admin'
    const isAdminOrManager = unit.role === 'admin' || unit.role === 'manager'

    return (
      <DataCard
        icon={
          <ContextIcon
            context={(unit.context_key as UnitContext) || appContext}
            className="h-5 w-5 text-emerald-600 dark:text-emerald-400"
          />
        }
        title={unit.name}
        subtitle={getRoleLabel(unit.role)}
        fields={[
          ...(unit.description ? [{ label: tu('record.descriptionLabel'), value: unit.description }] : []),
          // Only show context field in multi-context mode
          ...(!fixedContext ? [{
            label: 'Contexto',
            value: unit.context_key ? t(`contexts.${unit.context_key}`) : '—'
          }] : []),
          {
            label: 'Desde',
            value: new Date(unit.created_at).toLocaleDateString('pt-BR')
          }
        ]}
        actions={[
          ...(isAdminOrManager ? [{
            label: tu('list.manageTeam'),
            icon: (
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            ),
            onClick: () => setTeamUnit(unit)
          }] : []),
          ...(isAdmin ? [{
            label: tu('record.updateContext'),
            icon: (
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
              </svg>
            ),
            onClick: () => setContextUnit(unit)
          // Only show context update action in multi-context mode
          }] : []).filter(() => !fixedContext),
          ...(isAdmin && fixedContext === 'pricing' ? [{
            label: tu('list.updateActivity'),
            icon: (
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            ),
            onClick: () => setActivityUnit(unit)
          }] : []),
          ...(isAdmin && fixedContext === 'pricing' ? [{
            label: tu('list.updateDefaultMargin'),
            icon: (
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            ),
            onClick: () => setMarginUnit(unit)
          }] : []),
          ...(isAdmin ? [{
            label: tu('list.updateGroups'),
            icon: (
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
              </svg>
            ),
            onClick: () => setGroupsUnit(unit)
          }] : []).filter(() => fixedContext !== 'billing' && fixedContext !== 'pricing'),
          ...(isAdmin ? [{
            label: t('actions.edit'),
            icon: (
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            ),
            onClick: () => onEdit(unit)
          }] : []),
          ...(isAdmin ? [{
            label: t('actions.delete'),
            icon: (
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            ),
            onClick: () => onDelete(unit),
            variant: 'danger' as const
          }] : [])
        ]}
      />
    )
  }

  const renderForm = (
    formData: UnitFormData,
    setFormData: (d: UnitFormData) => void,
    formErrors: Record<string, string>,
    submitting: boolean,
    _editingEntity: UserUnit | null,
    mode: 'create' | 'edit' | 'view'
  ) => (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
          {tu('record.nameLabel')} <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          placeholder={tu('record.namePlaceholder', { unit: tu('singular') })}
          className={`w-full px-4 py-2 rounded-lg border ${
            formErrors.name ? 'border-red-300' : 'border-slate-300'
          } dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500`}
          disabled={submitting || mode === 'view'}
          required
          minLength={3}
        />
        {formErrors.name && <p className="mt-1 text-sm text-red-600">{formErrors.name}</p>}
      </div>
      <div>
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
          {tu('record.descriptionLabel')}
        </label>
        <textarea
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          placeholder={tu('record.descriptionPlaceholder', { unit: tu('singular').toLowerCase() })}
          rows={3}
          className="w-full px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none"
          disabled={submitting || mode === 'view'}
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
          {t('pix.label')}
        </label>
        <input
          type="text"
          value={formData.pix_key}
          onChange={(e) => setFormData({ ...formData, pix_key: e.target.value })}
          placeholder={t('pix.placeholder')}
          className="w-full px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
          disabled={submitting || mode === 'view'}
        />
      </div>
    </div>
  )

  return (
    <MainLayout userName={authUser.email} userId={authUser.id}>
      {invitations.length > 0 && (
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
          <InvitationsList
            invitations={invitations}
            onAccept={async (id) => { await acceptInvitation(id); await reloadInvitations() }}
            onDecline={async (id) => { await declineInvitation(id); await reloadInvitations() }}
          />
        </div>
      )}

      <EntityListPage<UserUnit, UnitFormData>
        key={reloadKey}
        config={config}
        labels={labels}
        icon={
          <ContextIcon
            context={appContext}
            className="h-7 w-7 text-emerald-600 dark:text-emerald-400"
          />
        }
        filterFields={filterFields}
        tableColumns={tableColumns}
        canEdit={(unit) => unit.role === 'admin'}
        canDelete={(unit) => unit.role === 'admin'}
        enableBulkSelection={false}
        cardFields={['description', 'context', 'role', 'created']}
        renderCard={renderCard}
        renderForm={renderForm}
        entityToFormData={(unit) => ({
          name: unit.name,
          description: unit.description || '',
          pix_key: unit.pix_key || ''
        })}
        emptyFormData={() => ({ name: '', description: '', pix_key: '' })}
        namespace="units"
      />

      {/* Unit management modals */}
      <TeamModal
        unitId={teamUnit?.id ?? ''}
        isOpen={!!teamUnit}
        onClose={() => setTeamUnit(null)}
      />

      <ContextUpdateModal
        unitId={contextUnit?.id ?? ''}
        currentContextKey={contextUnit?.context_key}
        isOpen={!!contextUnit}
        onClose={() => setContextUnit(null)}
        onUpdateContext={async (contextKey) => {
          if (contextUnit) await updateUnitContext(contextUnit.id, contextKey)
          setReloadKey(k => k + 1)
        }}
      />

      <GroupUpdateModal
        unitId={groupsUnit?.id ?? ''}
        currentContextKey={groupsUnit?.context_key}
        isOpen={!!groupsUnit}
        onClose={() => setGroupsUnit(null)}
        onUpdateGroups={async (unitId, groupIds) => {
          await saveEnabledGroups(unitId, groupIds)
          setReloadKey(k => k + 1)
        }}
      />

      <ActivityUpdateModal
        unitId={activityUnit?.id ?? ''}
        isOpen={!!activityUnit}
        onClose={() => setActivityUnit(null)}
        onUpdated={() => setReloadKey(k => k + 1)}
      />

      <DefaultMarginUpdateModal
        unitId={marginUnit?.id ?? ''}
        isOpen={!!marginUnit}
        onClose={() => setMarginUnit(null)}
        onUpdated={() => setReloadKey(k => k + 1)}
      />
    </MainLayout>
  )
}
